import type {
  AnalyticsEventMap,
  AnalyticsEventName,
  AnalyticsEventParameters,
} from "./events";

export const ANALYTICS_CONSENT_STORAGE_KEY = "geooffice.analytics-consent.v1";

export type AnalyticsConsent = "granted" | "denied";

// A medicao de uso e opt-out: sem escolha salva a coleta roda. So uma recusa
// explicita desliga. O modelo anterior era opt-in e, como quase ninguem
// respondia ao aviso, a propriedade do GA4 ficava praticamente vazia.
export const DEFAULT_ANALYTICS_CONSENT: AnalyticsConsent = "granted";

export const GA_MEASUREMENT_ID =
  process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID?.trim() ?? "";

let isInitialized = false;

export function isAnalyticsEnabled() {
  return Boolean(GA_MEASUREMENT_ID);
}

// O gtag.js so trata como comando o que chega no dataLayer como objeto
// `arguments`: ele testa `[object Arguments]` antes de despachar `config`,
// `event`, `js` e `consent`. Um array comum cai no ramo legado de chamada de
// metodo, falha e e engolido por um try/catch, sem erro no console e sem
// requisicao. Empilhar `arguments` aqui nao e estilo, e o que faz a tag
// funcionar.
export function createGtagStub(): (...args: unknown[]) => void {
  return function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
}

export function initializeGoogleAnalytics() {
  if (
    !isAnalyticsEnabled() ||
    typeof window === "undefined" ||
    !hasAnalyticsConsent()
  ) {
    return;
  }

  window.dataLayer = window.dataLayer ?? [];
  window.gtag = window.gtag ?? createGtagStub();

  setGoogleAnalyticsCollectionEnabled(true);

  if (isInitialized) return;
  isInitialized = true;

  // Consent Mode v2. O estado de analise acompanha a escolha do usuario e as
  // categorias de anuncio ficam sempre negadas: a integracao e de produto.
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
}

export function trackEvent<Name extends AnalyticsEventName>(
  name: Name,
  parameters: AnalyticsEventMap[Name],
) {
  if (
    !isAnalyticsEnabled() ||
    typeof window === "undefined" ||
    !window.gtag ||
    !hasAnalyticsConsent()
  ) {
    return;
  }

  window.gtag("event", name, removeUndefined(parameters));
}

export function trackPageView(pathname: string) {
  if (
    !isAnalyticsEnabled() ||
    typeof window === "undefined" ||
    !window.gtag ||
    !hasAnalyticsConsent()
  ) {
    return;
  }

  const normalizedPath = normalizeAnalyticsPath(pathname);
  if (!normalizedPath) return;

  window.gtag("event", "page_view", {
    page_location: `${window.location.origin}${normalizedPath}`,
    page_path: normalizedPath,
    page_title: normalizedPath,
    page_referrer: safeReferrer(document.referrer),
  });
}

export function updateGoogleAnalyticsConsent(consent: AnalyticsConsent) {
  if (typeof window === "undefined") return;

  const enabled = consent === "granted";
  setGoogleAnalyticsCollectionEnabled(enabled);

  // A recusa precisa limpar cookie mesmo quando a tag nunca carregou nesta
  // aba; sair cedo por falta de `gtag` deixava `_ga` de uma visita anterior.
  window.gtag?.("consent", "update", {
    analytics_storage: consent,
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });

  if (!enabled) {
    removeAnalyticsCookies();
  }
}

export function setGoogleAnalyticsCollectionEnabled(enabled: boolean) {
  if (typeof window === "undefined" || !GA_MEASUREMENT_ID) return;

  (window as unknown as Record<string, unknown>)[
    `ga-disable-${GA_MEASUREMENT_ID}`
  ] = !enabled;
}

export function normalizeAnalyticsPath(pathname: string): string | null {
  const path = pathname.split(/[?#]/, 1)[0] || "/";

  if (
    path === "/share" ||
    path.startsWith("/share/") ||
    path.startsWith("/auth/reset-password")
  ) {
    return null;
  }

  return path
    .replace(/^\/account\/[^/]+/, "/account/:account")
    .replace(/\/organizations\/[^/]+/, "/organizations/:organization")
    .replace(/\/workspace-files\/[^/]+/, "/workspace-files/:workspace")
    .replace(/\/(dwg|scene-3d|reconstitution|downloads)\/[^/]+/, "/$1/:item")
    .replace(/\/memorial\/(?!novo(?:\/|$))[^/]+/, "/memorial/:item");
}

function safeReferrer(referrer: string) {
  if (!referrer) return "";

  try {
    return new URL(referrer).origin;
  } catch {
    return "";
  }
}

export function readStoredAnalyticsConsent(): AnalyticsConsent | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    return stored === "granted" || stored === "denied" ? stored : null;
  } catch {
    // Navegacao privada e navegadores com storage bloqueado lancam aqui. Nesse
    // caso vale o padrao, e nao um desligamento silencioso.
    return null;
  }
}

export function resolveAnalyticsConsent(
  stored: AnalyticsConsent | null,
): AnalyticsConsent {
  return stored ?? DEFAULT_ANALYTICS_CONSENT;
}

export function storeAnalyticsConsent(consent: AnalyticsConsent) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, consent);
  } catch {
    // Sem persistencia a escolha vale so para a sessao atual.
  }
}

function hasAnalyticsConsent() {
  return resolveAnalyticsConsent(readStoredAnalyticsConsent()) === "granted";
}

function removeAnalyticsCookies() {
  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=", 1)[0]?.trim();

    if (name === "_ga" || name?.startsWith("_ga_")) {
      document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
    }
  }
}

function removeUndefined(parameters: AnalyticsEventParameters) {
  return Object.fromEntries(
    Object.entries(parameters).filter(([, value]) => value !== undefined),
  );
}
